import { HttpClient, HttpEventType, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CacheKeyService } from '../cache-key.service';
import { DBService } from '../db.service';
import apiPaths from 'constants/api-paths';
import { appDb } from 'dexie/app.db';
import { generateURL } from 'helpers/api-path.helper';
import {
  switchMap,
  from,
  of,
  catchError,
  map,
  Observable,
  throwError,
  filter,
} from 'rxjs';
import { WarehouseApiService } from './warehouse-api.service';
import { CompanyApiService } from './firms-api.service';

@Injectable({
  providedIn: 'root',
})
export class WarehouseReportApiService {
  protected dbService = inject(DBService);
  protected cacheKeyService = inject(CacheKeyService);
  protected http = inject(HttpClient);
  protected warehouseApiService = inject(WarehouseApiService);
  protected companyApiService = inject(CompanyApiService);

  services = {
    companyKey: (c) => this.companyApiService.getFirms(c),
    wareHouseKey: (c) => this.warehouseApiService.getWareHouseList(c),
  };
  constructor() {}

  getList(cacheKey?: string) {
    return this.http
      .get(
        generateURL(
          apiPaths.warehouseReportList,
          {},
          new HttpParams({ fromObject: { cacheKey } })
        )
      )
      .pipe(
        switchMap((data: any) => {
          if (data.isAllDb) {
            return from(
              appDb.warehouseReport.clear().then(() =>
                appDb.warehouseReport.bulkAdd(
                  data.items.map((item) => ({
                    ...item,
                    isSynced: 1,
                    isDeleted: item.isDeleted ? 1 : 0,
                  }))
                )
              )
            );
          }
          return from(
            appDb.warehouseReport.bulkPut(
              data.items.map((item) => ({
                ...item,
                isSynced: 1,
                isDeleted: item.isDeleted ? 1 : 0,
              }))
            )
          );
        }),
        catchError(() => {
          return of(null);
        })
      );
  }

  create(data): Observable<any> {
    return this.http.post(generateURL(apiPaths.warehouseReport), data).pipe(
      switchMap((res) =>
        this.dbService.getRow(appDb.cacheKeys, 'wareHouseReportKey').pipe(
          switchMap((cache) => {
            return this.getList(cache.hash).pipe(map(() => res));
          })
        )
      ),
      switchMap((res) =>
        this.cacheKeyService
          .checkAndUpdateTables(undefined, this.services)
          .pipe(map(() => res))
      ),
      catchError((err) => throwError(() => err))
    );
  }
  update(id, data) {
    return this.http
      .put(generateURL(apiPaths.warehouseReportId, { id }), data)
      .pipe(
        switchMap((res) =>
          this.dbService.getRow(appDb.cacheKeys, 'wareHouseReportKey').pipe(
            switchMap((cache) => {
              return this.getList(cache.hash).pipe(map(() => res));
            })
          )
        ),
        switchMap((res) =>
          this.cacheKeyService
            .checkAndUpdateTables(undefined, this.services)
            .pipe(map(() => res))
        ),
        catchError((err) => throwError(() => err))
      );
  }
  delete(id) {
    return this.http
      .delete(generateURL(apiPaths.warehouseReportId, { id }))
      .pipe(
        switchMap((res) =>
          this.dbService.getRow(appDb.cacheKeys, 'wareHouseReportKey').pipe(
            switchMap((cache) => {
              return this.getList(cache.hash).pipe(map(() => res));
            })
          )
        ),
        switchMap((res) =>
          this.cacheKeyService
            .checkAndUpdateTables(undefined, this.services)
            .pipe(map(() => res))
        ),
        catchError((err) => throwError(() => err))
      );
  }

  move(wareHouseReportId, fromWareHouseId, toWareHouseId) {
    return this.http
      .put(generateURL(apiPaths.warehouseReportMove), {
        wareHouseReportId,
        fromWareHouseId,
        toWareHouseId,
      })
      .pipe(
        switchMap((res) =>
          this.dbService.getRow(appDb.cacheKeys, 'wareHouseReportKey').pipe(
            switchMap((cache) => {
              return this.getList(cache.hash).pipe(map(() => res));
            })
          )
        ),
        switchMap((res) =>
          this.cacheKeyService
            .checkAndUpdateTables(undefined, this.services)
            .pipe(map(() => res))
        ),
        catchError((err) => throwError(() => err))
      );
  }

  sign(wareHouseReportId, doc: File, extraFileName: string) {
    const formData = new FormData();
    formData.append('file', doc);
    if (extraFileName) {
      formData.append('fileName', extraFileName);
    }
    return this.http
      .put(
        generateURL(apiPaths.warehouseReportSign, { id: wareHouseReportId }),
        formData,
        {
          reportProgress: true, // Crucial for tracking upload progress
          observe: 'events', // Observe all events (including progress events)
        }
      )
      .pipe(
        filter((event) => event.type === HttpEventType.Response),
        map((event) => event.body as string),
        switchMap((res) =>
          this.dbService.getRow(appDb.cacheKeys, 'wareHouseReportKey').pipe(
            switchMap((cache) => {
              return this.getList(cache.hash).pipe(map(() => res));
            })
          )
        ),
        switchMap((res) =>
          this.cacheKeyService
            .checkAndUpdateTables(undefined, this.services)
            .pipe(map(() => res))
        ),
        catchError((err) => throwError(() => err))
      );
  }

  bulk(reports) {
    return this.http.post(
      generateURL(apiPaths.warehouseReportBulk),
      reports.map((item) => ({
        ...item,
        isDeleted: item.isDeleted ? true : false,
      }))
    );
  }
}
